import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Seo from '../Components/Seo';
import fallbackImage from '../img/about-banner.png';

const API_ROOTS = {
  signulu: 'https://blog.signulu.com/wp-json/wp/v2',
  signuluone: 'https://blogs.signuluone.com/wp-json/wp/v2',
};

// Category/tag pill bar is disabled for now — see the commented block below and the
// "categories" section further down in this file to bring it back.
// const TAGS_API = `${WP_BASE}/tags`;

// Two independent WordPress sources are merged into one feed: SignuluOne posts
// (on/after the cutoff below, excluding anything in the "BulkSigner" category) are
// shown first, followed by normal Signulu posts. Each source is fetched and paged
// independently — a small first request per source for a fast initial paint, then
// the rest of that source's pages are pulled in the background with no pagination UI.
const STREAM_SIGNULUONE = 'signuluOne';
const STREAM_SIGNULU = 'signulu';

const INITIAL_PAGE_SIZE = 6;
const BACKGROUND_PAGE_SIZE = 100;

// Only applies to blogs.signuluone.com posts — normal Signulu posts have no cutoff.
const SIGNULUONE_SIGNULU_CUTOFF = '2026-09-03T00:00:00';

// const DEFAULT_CATEGORY_NAME = 'Audit Trail';
// How many category pages we keep warm in sessionStorage between visits.
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const SESSION_CACHE_PREFIX = 'signulu_blog_cache_v2:';
// Background prefetch runs this many requests at once, only during idle time.
// Kept low because the WordPress API on this host is slow per-request (multi-second
// with embeds) — piling on concurrent background requests just slows everything down.
// const PREFETCH_CONCURRENCY = 1;

function stripHtml(html) {
  const holder = document.createElement('div');
  holder.innerHTML = html || '';
  return (holder.textContent || '').replace(/\s+/g, ' ').trim();
}

function truncate(text, max) {
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

function readingTime(html) {
  const words = stripHtml(html).split(' ').filter(Boolean).length;
  return Math.max(1, Math.round(words / 200)) + ' min read';
}

function formatDate(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getEmbeddedTerms(post) {
  const groups = (post._embedded && post._embedded['wp:term']) || [];
  return groups
    .reduce((all, group) => all.concat(group || []), [])
    .map((t) => ({ ...t, name: stripHtml(t.name || '') }));
}

function getCategories(post) {
  return getEmbeddedTerms(post).filter((t) => t.taxonomy === 'category');
}

function getPrimaryCategoryName(post) {
  const cats = getCategories(post);
  return (cats[0] && cats[0].name) || 'Signulu';
}

function getImage(post) {
  const media = post._embedded && post._embedded['wp:featuredmedia'];
  const item = media && media[0];
  return (item && item.source_url) || fallbackImage;
}

// Dedupe by source+id (the same numeric WordPress id can exist on both API roots)
// and sort newest-first. Used at render time so each stream's own background
// pagination can land independently without needing to pre-sort a merged array.
function dedupeAndSortDesc(posts) {
  const byKey = new Map();
  posts.forEach((p) => byKey.set(`${p._source}:${p.id}`, p));
  return [...byKey.values()].sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Only used by the disabled background-prefetch queue below.
// function runIdle(fn) {
//   if (typeof window.requestIdleCallback === 'function') {
//     return window.requestIdleCallback(fn, { timeout: 2000 });
//   }
//   return setTimeout(fn, 250);
// }

function readSessionCache(key) {
  try {
    const raw = window.sessionStorage.getItem(SESSION_CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeSessionCache(key, data) {
  try {
    window.sessionStorage.setItem(SESSION_CACHE_PREFIX + key, JSON.stringify({ ...data, ts: Date.now() }));
  } catch (e) {
    // storage full or unavailable — safe to ignore, it's only a warm-start optimization
  }
}

function postsUrl({ root, page, perPage, categoryId, excludeCategoryId, afterDate }) {
  const params = new URLSearchParams();
  params.set('per_page', String(perPage));
  params.set('page', String(page));
  params.set('orderby', 'date');
  params.set('order', 'desc');
  params.set('_embed', 'wp:featuredmedia,wp:term');
  params.set('_fields', 'id,date,slug,link,title,excerpt,categories,tags,_links,_embedded');
  if (categoryId) params.set('categories', String(categoryId));
  if (excludeCategoryId) params.set('categories_exclude', String(excludeCategoryId));
  if (afterDate) params.set('after', afterDate);
  return `${root}/posts?${params.toString()}`;
}

/* Category/tag pill bar — commented out for now (flat article feed instead).
// The tags endpoint caps at 100 per page; this site has 126+, so page through all of them.
async function fetchAllTags() {
  const all = [];
  let page = 1;
  // Small safety cap so a misbehaving API can't loop forever.
  for (; page <= 10; page += 1) {
    const url = `${TAGS_API}?per_page=100&page=${page}&orderby=name&order=asc&hide_empty=true&_fields=id,name,slug,count`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Tag list request failed: ' + res.status);
    const data = await res.json();
    all.push(...(Array.isArray(data) ? data : []));
    const totalPages = Number(res.headers.get('X-WP-TotalPages')) || 1;
    if (page >= totalPages) break;
  }
  return all;
}

// WordPress has 100+ near-duplicate tags (eSign / eSignature / e-Signing / Electronic
// Signature / ...). Showing all of them as pills is unusable, so we fold them into a
// curated set of topic groups. A group's pill queries the union of its member tags'
// post ids (WordPress's `tags` param already does an OR across a comma-separated list).
const TAG_GROUPS = [
  { name: 'Electronic Signature', tags: ['Aadhaar e-Sign', 'Digital Signatures', 'Digital Transactions', 'digital transformation', 'e signature', 'e Signing', 'Electronic Signature', 'electronic signature solutions', 'eSign', 'eSignature', 'ESignatures', 'electronic document', 'E-Stamping', 'eStamp', 'eStamping'] },
  { name: 'Document Management', tags: ['Document Management', 'Document Summarization', 'Forms', 'Transcripts', 'cloud', 'Cloud Storage', 'Internal Storage', 'Google Drive', 'Dropbox', 'Microsoft Onedrive'] },
  { name: 'Contract Management', tags: ['Contract Lifecycle Management', 'Contract Management', 'Contracts', 'Master Service Agreements', 'MSA', 'NDA', 'Non-Disclosure Agreements', 'Partner Agreements', 'Service Agreement', 'Vendor Agreement', 'Vendor Management', 'Procurement', 'Purchase', 'Renewal', 'MOU', 'Cross-Border Agreements'] },
  { name: 'Compliance & Data Privacy', tags: ['Compliance', 'Audit Trail', 'Certificates', 'Confidential', 'Data Privacy', 'Client Protection', 'patient Data', 'Patient Records'] },
  { name: 'Cyber Security', tags: ['Cyber Security', 'Encryption', 'Secure', 'Verified', 'Blockchain', 'Smart Contracts'] },
  { name: 'AI & Automation', tags: ['AI', 'Artificial Intelligence', 'GenAI', 'GenAI Chatbot', 'GenAI-powered', 'Generative AI', 'Process Automation', 'Approval Process', 'ERP'] },
  { name: 'Legal', tags: ['Law', 'Law Firm', 'Legal', 'LegalTech', 'Intellectual Property'] },
  { name: 'Mergers & Acquisitions', tags: ['Mergers', 'Acquisition', 'Due Diligence'] },
  { name: 'HR & Remote Work', tags: ['HR', 'Human Resources', 'Onboarding', 'Freelancers', 'Gig Economy', 'Remote Job', 'Remote Work', 'WFH'] },
  { name: 'Startups & SMBs', tags: ['Scaling Business', 'Small and Medium Businesses', 'Small And Midsize Business', 'Small Businesses', 'SMB', 'Startup', 'Startups', 'Subscription-Based Businesses', 'Customer Experience'] },
  { name: 'Healthcare Industry', tags: ['Health Industry', 'Healthcare', 'HMS', 'Hospital Management Software', 'Pharma', 'Device Management Solution'] },
  { name: 'Finance & Insurance', tags: ['Finance', 'Accounting', 'Insurance'] },
  { name: 'Real Estate & Construction', tags: ['Real Estate', 'Construction'] },
  { name: 'Retail & eCommerce', tags: ['Retail', 'FMCG', 'eCommerce'] },
  { name: 'Manufacturing & Supply Chain', tags: ['Manufacturing Industry', 'Supply Chain', 'Supply Chain Management', 'Logistics', 'Transport', 'Transport Industry', 'Trade', 'Global Business'] },
  { name: 'Franchising', tags: ['Franchise Management', 'Franchising'] },
  { name: 'Government & Nonprofit', tags: ['Government', 'NGO', 'Nonprofit', 'Nonprofit Organization'] },
  { name: 'Creative Industry', tags: ['Creative Industry', 'Protecting Creative Assets', 'Interior designing'] },
  { name: 'Sustainability', tags: ['Eco-friendly', 'Sustainable Solution'] },
  { name: 'Technology', tags: ['IT Industry', 'Mobile App'] },
  { name: 'Education', tags: ['Education'] },
];

// Turns the raw ~125 WordPress tags into the curated groups above. Any tag that
// doesn't match a group (a brand-new tag added later, a naming tweak) still shows up,
// under a catch-all "More" pill, so nothing published ever becomes unreachable.
function buildTagGroups(rawTags) {
  const byName = new Map(rawTags.map((t) => [t.name.toLowerCase(), t]));
  const used = new Set();
  const groups = [];

  TAG_GROUPS.forEach(({ name, tags }) => {
    const ids = [];
    tags.forEach((tagName) => {
      const match = byName.get(tagName.toLowerCase());
      if (match) {
        ids.push(match.id);
        used.add(match.id);
      }
    });
    if (ids.length) groups.push({ id: ids.join(','), name, tagIds: ids });
  });

  const leftover = rawTags.filter((t) => !used.has(t.id));
  if (leftover.length) {
    groups.push({ id: leftover.map((t) => t.id).join(','), name: 'More', tagIds: leftover.map((t) => t.id) });
  }

  return groups;
}
*/

function DemoCtaBand() {
  return (
    <section className="blog-cta-band">
      <div className="container">
        <div className="blog-cta-inner">
          <div>
            <h2>Prefer a live walkthrough?</h2>
            <p>We will show the flows behind these articles on your own documents.</p>
          </div>
          <Link className="blog-cta-btn" to="/contact">Book a demo</Link>
        </div>
      </div>
    </section>
  );
}

function BlogCard({ post, onSelect }) {
  const title = stripHtml(post.title && post.title.rendered) || 'Untitled';
  const excerpt = truncate(stripHtml(post.excerpt && post.excerpt.rendered), 140);

  return (
    <div className="col-md-6 col-lg-4">
      <div className="blog-card" onClick={() => onSelect(post)}>
        <img src={getImage(post)} alt={title} loading="lazy" />
        <div className="blog-card-body">
          <span className="tag-pill">{getPrimaryCategoryName(post)}</span>
          <h3 className="blog-card-title">{title}</h3>
          {excerpt && <p className="blog-card-excerpt">{excerpt}</p>}
          <p className="blog-meta">{formatDate(post.date)}</p>
        </div>
      </div>
    </div>
  );
}

function BlogPostDetail({ post, detail, detailStatus, onBack }) {
  const title = stripHtml(post.title && post.title.rendered) || 'Untitled';
  const body = detail && detail.content && detail.content.rendered;

  return (
    <section className="blog-post-section">
      <div className="container blog-post-container">
        <button type="button" className="blog-back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i> Back to Blog
        </button>
        <h1 className="blog-post-title">{title}</h1>
        <p className="blog-post-meta">
          {getPrimaryCategoryName(post)} &nbsp;&middot;&nbsp; {formatDate(post.date)}
          {body && <>&nbsp;&middot;&nbsp; {readingTime(body)}</>}
        </p>
        <img src={getImage(post)} alt={title} className="blog-post-image" loading="eager" />
        {detailStatus === 'loading' && <p className="blog-status-text">Loading article…</p>}
        {detailStatus === 'error' && (
          <p className="blog-status-text">This article could not be loaded. Please try again later.</p>
        )}
        {detailStatus === 'ready' && body && (
          <div className="blog-post-body" dangerouslySetInnerHTML={{ __html: body }} />
        )}
      </div>
    </section>
  );
}

function Blog() {
  // Categories/tags pill bar is disabled — flat merged feed always shown.
  // const [categories, setCategories] = useState([]);
  // const [categoriesStatus, setCategoriesStatus] = useState('loading'); // loading | ready | error

  // catalog: { [stream]: { status, posts, page, totalPages, error, stale } }
  // stream is STREAM_SIGNULUONE or STREAM_SIGNULU — each source's pages accumulate
  // independently and are only merged together at render time.
  const [catalog, setCatalog] = useState({});
  const catalogRef = useRef({});

  // Resolved once via GET signuluone/categories?slug=bulksigner, then used to keep
  // BulkSigner-category posts out of the merged feed (both server-side via
  // categories_exclude and again client-side as a defense-in-depth check).
  const [bulkSignerCategoryId, setBulkSignerCategoryId] = useState(null);
  const bulkSignerCategoryIdPromiseRef = useRef(null);

  // Pure guards for the sequential background page-walkers — no re-render needed.
  const backgroundLoadingRef = useRef({ [STREAM_SIGNULUONE]: false, [STREAM_SIGNULU]: false });

  const [selectedPost, setSelectedPost] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');
  const detailCacheRef = useRef(new Map());

  const inFlightRef = useRef(new Map());
  const mountedRef = useRef(true);

  const updateCatalog = useCallback((key, patch) => {
    if (!mountedRef.current) return;
    setCatalog((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), ...patch } };
      catalogRef.current = next;
      return next;
    });
  }, []);

  const fetchPostsPage = useCallback((stream, { root, source, page, perPage, categoryId, excludeCategoryId, afterDate }) => {
    const dedupeKey = stream + ':' + page;
    if (inFlightRef.current.has(dedupeKey)) return inFlightRef.current.get(dedupeKey);

    const existing = catalogRef.current[stream];
    if (existing && existing.status === 'loaded' && existing.page >= page && !existing.stale) {
      return Promise.resolve();
    }

    // Warm-start from sessionStorage so a repeat visit in this tab renders instantly,
    // then still revalidate in the background so content stays fresh.
    let hydrated = false;
    if (page === 1 && !existing) {
      const cached = readSessionCache(stream);
      if (cached) {
        hydrated = true;
        updateCatalog(stream, {
          status: 'loaded',
          posts: cached.posts,
          page: cached.page,
          totalPages: cached.totalPages,
          error: null,
          stale: true,
        });
      }
    }

    if (!hydrated) {
      updateCatalog(stream, { status: existing && existing.posts ? 'loading-more' : 'loading', error: null });
    }

    const promise = fetch(postsUrl({ root, page, perPage, categoryId, excludeCategoryId, afterDate }))
      .then(async (res) => {
        if (!res.ok) throw new Error('Blog feed request failed: ' + res.status);
        const data = await res.json();
        const totalPagesHeader = Number(res.headers.get('X-WP-TotalPages'));
        const totalPages = Number.isFinite(totalPagesHeader) && totalPagesHeader > 0 ? totalPagesHeader : page;
        const fetchedPosts = (Array.isArray(data) ? data : []).map((p) => ({ ...p, _source: source }));
        const prevPosts = page > 1 ? (catalogRef.current[stream] && catalogRef.current[stream].posts) || [] : [];
        const posts = page > 1 ? prevPosts.concat(fetchedPosts) : fetchedPosts;
        updateCatalog(stream, { status: 'loaded', posts, page, totalPages, error: null, stale: false });
        if (page === 1) writeSessionCache(stream, { posts, page, totalPages });
      })
      .catch((err) => {
        // If we already have stale cached content on screen, keep showing it rather than an error.
        const current = catalogRef.current[stream];
        if (current && current.status === 'loaded' && current.posts && current.posts.length) return;
        updateCatalog(stream, { status: 'error', error: err.message });
      })
      .finally(() => {
        inFlightRef.current.delete(dedupeKey);
      });

    inFlightRef.current.set(dedupeKey, promise);
    return promise;
  }, [updateCatalog]);

  // Walks pages 2..totalPages for one source, sequentially, entirely in the
  // background — no pagination UI, no "Load more" button. Each landed page updates
  // `catalog`, which re-renders the grid automatically if new posts are now visible.
  const walkBackgroundPages = useCallback((stream, opts) => {
    if (backgroundLoadingRef.current[stream]) return;
    backgroundLoadingRef.current[stream] = true;

    const step = (page) => {
      const entry = catalogRef.current[stream];
      const totalPages = (entry && entry.totalPages) || 1;
      if (page > totalPages) {
        backgroundLoadingRef.current[stream] = false;
        return;
      }
      fetchPostsPage(stream, { ...opts, page, perPage: BACKGROUND_PAGE_SIZE }).then(() => {
        const latest = catalogRef.current[stream];
        if (latest && latest.status === 'error') {
          backgroundLoadingRef.current[stream] = false;
          return;
        }
        step(page + 1);
      });
    };

    step(2);
  }, [fetchPostsPage]);

  // Memoized so it's only ever requested once, however many callers ask for it.
  const resolveBulkSignerCategoryId = useCallback(() => {
    if (bulkSignerCategoryIdPromiseRef.current) return bulkSignerCategoryIdPromiseRef.current;

    const promise = fetch(`${API_ROOTS.signuluone}/categories?slug=bulksigner&_fields=id`)
      .then((res) => {
        if (!res.ok) throw new Error('BulkSigner category lookup failed: ' + res.status);
        return res.json();
      })
      .then((cats) => {
        const id = (Array.isArray(cats) && cats[0] && cats[0].id) || null;
        if (mountedRef.current) setBulkSignerCategoryId(id);
        return id;
      })
      .catch((err) => {
        console.error(err);
        // Best-effort: proceed without the exclude filter rather than blocking the feed.
        return null;
      });

    bulkSignerCategoryIdPromiseRef.current = promise;
    return promise;
  }, []);

  // Load both sources on mount. The BulkSigner category id is resolved first so the
  // SignuluOne request can exclude it; the normal Signulu request never needs it.
  useEffect(() => {
    mountedRef.current = true;

    resolveBulkSignerCategoryId().then((bulkId) => {
      if (!mountedRef.current) return;

      fetchPostsPage(STREAM_SIGNULUONE, {
        root: API_ROOTS.signuluone,
        source: 'signuluone',
        page: 1,
        perPage: INITIAL_PAGE_SIZE,
        afterDate: SIGNULUONE_SIGNULU_CUTOFF,
        excludeCategoryId: bulkId,
      }).then(() => {
        walkBackgroundPages(STREAM_SIGNULUONE, {
          root: API_ROOTS.signuluone,
          source: 'signuluone',
          afterDate: SIGNULUONE_SIGNULU_CUTOFF,
          excludeCategoryId: bulkId,
        });
      });

      fetchPostsPage(STREAM_SIGNULU, {
        root: API_ROOTS.signulu,
        source: 'signulu',
        page: 1,
        perPage: INITIAL_PAGE_SIZE,
      }).then(() => {
        walkBackgroundPages(STREAM_SIGNULU, {
          root: API_ROOTS.signulu,
          source: 'signulu',
        });
      });
    });

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = selectedPost
      ? `${stripHtml(selectedPost.title && selectedPost.title.rendered)} | Signulu Blog`
      : 'Blog — eSignature Insights & Product News | Signulu';
  }, [selectedPost]);

  // const pillCategories = useMemo(() => [{ id: ALL_KEY, name: 'All' }, ...categories], [categories]);

  const soEntry = catalog[STREAM_SIGNULUONE];
  const sEntry = catalog[STREAM_SIGNULU];

  // Re-derived every render, never stored merged: each source is filtered/deduped/
  // sorted independently, then concatenated with SignuluOne first. This keeps the
  // ordering invariant simple even as background pages land in either source at
  // different times.
  const visiblePosts = useMemo(() => {
    const cutoffTime = new Date(SIGNULUONE_SIGNULU_CUTOFF).getTime();
    const signuluOnePosts = dedupeAndSortDesc(
      ((soEntry && soEntry.posts) || []).filter((post) => {
        const postTime = new Date(post.date).getTime();
        if (isNaN(postTime) || postTime < cutoffTime) return false;
        if (bulkSignerCategoryId && (post.categories || []).includes(bulkSignerCategoryId)) return false;
        return true;
      })
    );
    const signuluPosts = dedupeAndSortDesc((sEntry && sEntry.posts) || []);
    return [...signuluOnePosts, ...signuluPosts];
  }, [soEntry, sEntry, bulkSignerCategoryId]);

  const soHasPosts = !!(soEntry && soEntry.posts && soEntry.posts.length);
  const sHasPosts = !!(sEntry && sEntry.posts && sEntry.posts.length);
  const soSettled = !!(soEntry && (soEntry.status === 'loaded' || soEntry.status === 'error'));
  const sSettled = !!(sEntry && (sEntry.status === 'loaded' || sEntry.status === 'error'));
  const bothSettled = soSettled && sSettled;

  // Spinner only while neither source has produced a post yet. Once either source
  // lands, show what's available — the other keeps loading silently in the background.
  const isInitialLoad = !soHasPosts && !sHasPosts && !bothSettled;
  const bothFailed = !soHasPosts && !sHasPosts && bothSettled && soEntry.status === 'error' && sEntry.status === 'error';
  const bothEmpty = !soHasPosts && !sHasPosts && bothSettled && !bothFailed;

  /* const handleCategoryClick = (cat) => {
    const key = String(cat.id);
    if (activeKey === key) return;
    setActiveCategory({ id: cat.id, name: cat.name });
    ensureCategoryLoaded(key, cat.id);
  }; */

  const handleSelect = async (post) => {
    setSelectedPost(post);
    setDetail(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const cacheKey = `${post._source}:${post.id}`;
    const cached = detailCacheRef.current.get(cacheKey);
    if (cached) {
      setDetail(cached);
      setDetailStatus('ready');
      return;
    }

    setDetailStatus('loading');
    try {
      const root = API_ROOTS[post._source] || API_ROOTS.signulu;
      const res = await fetch(`${root}/posts/${post.id}?_fields=id,content`);
      if (!res.ok) throw new Error('Post request failed: ' + res.status);
      const data = await res.json();
      detailCacheRef.current.set(cacheKey, data);
      setDetail(data);
      setDetailStatus('ready');
    } catch (e) {
      setDetailStatus('error');
    }
  };

  const handleBack = () => {
    setSelectedPost(null);
    setDetail(null);
    setDetailStatus('idle');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <Seo
        title="Signulu Blog | Electronic Signature & Workflow Insights"
        description="Read practical insights on electronic signatures, compliance, approval workflows and document management for legal, operations and business teams."
        canonical="https://www.signuluone.com/blog.html"
      />
      <Header />

      {selectedPost ? (
        <>
          <BlogPostDetail post={selectedPost} detail={detail} detailStatus={detailStatus} onBack={handleBack} />
          <DemoCtaBand />
        </>
      ) : (
        <>
          <section className="blog-hero">
            <div className="container">
              <span className="blog-eyebrow">Blog</span>
              <h1>Notes on signing, compliance and speed</h1>
              <p>Short, practical pieces for the people who own contracts and onboarding.</p>
            </div>
          </section>

          <section className="blog-grid-section">
            <div className="container">
              {/* Category pill bar — disabled for now, flat article feed instead.
              {categoriesStatus === 'loading' && <p className="blog-status-text">Loading categories…</p>}
              {categoriesStatus === 'ready' && pillCategories.length > 1 && (
                <div className="blog-filters">
                  {pillCategories.map((cat) => (
                    <button
                      type="button"
                      key={cat.id}
                      className={activeKey === String(cat.id) ? 'active' : ''}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
              */}

              {isInitialLoad && <p className="blog-status-text">Loading articles…</p>}
              {bothFailed && (
                <p className="blog-status-text">Articles are temporarily unavailable. Please try again later.</p>
              )}
              {bothEmpty && <p className="blog-status-text">Blog posts are coming soon.</p>}
              {!isInitialLoad && visiblePosts.length > 0 && (
                <div className="row g-4">
                  {visiblePosts.map((post) => (
                    <BlogCard key={`${post._source}:${post.id}`} post={post} onSelect={handleSelect} />
                  ))}
                </div>
              )}
            </div>
          </section>

          <DemoCtaBand />
        </>
      )}

      <Footer />
    </div>
  );
}

export default Blog;
